import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Megaphone, Plus, Calendar, CheckCircle2, XCircle, Clock, TrendingUp,
  AlertCircle, FileText, Scale, Users, ArrowRight, RefreshCw, Loader2,
  BarChart3, Target, Send, Eye, ListTodo, User, AlertTriangle
} from 'lucide-react';
import api, { marketingTasksAPI, usersAPI } from '../api';

const priorityColors = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-white',
  low: 'bg-gray-400 text-white'
};

const categoryColors = {
  campaign: 'bg-blue-100 text-blue-800',
  creative: 'bg-purple-100 text-purple-800',
  content: 'bg-green-100 text-green-800',
  email: 'bg-rose-100 text-rose-800',
  social: 'bg-pink-100 text-pink-800',
  analytics: 'bg-cyan-100 text-cyan-800',
  general: 'bg-gray-100 text-gray-800'
};

export const MarketingDashboardPage = () => {
  const { user, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  
  // Task state
  const [myTasks, setMyTasks] = useState([]);
  const [taskStats, setTaskStats] = useState({ total: 0, pending: 0, overdue: 0 });
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'medium',
    category: 'general',
    due_date: ''
  });
  const [creatingTask, setCreatingTask] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchMyTasks();
    fetchTeamMembers();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      const [dashRes, approvalsRes] = await Promise.all([
        api.get('/marketing/dashboard'),
        api.get('/marketing/approvals/pending')
      ]);
      setDashboardData(dashRes.data);
      setPendingApprovals(approvalsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load marketing dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const fetchMyTasks = async () => {
    try {
      const response = await marketingTasksAPI.getMyTasks(false, 10);
      setMyTasks(response.data.tasks || []);
      setTaskStats(response.data.stats || { total: 0, pending: 0, overdue: 0 });
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };
  
  const fetchTeamMembers = async () => {
    try {
      const response = await usersAPI.getTeam();
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };
  
  const handleCreateTask = async () => {
    if (!taskForm.title) {
      toast.error('Task title is required');
      return;
    }
    
    try {
      setCreatingTask(true);
      await marketingTasksAPI.create(taskForm);
      toast.success('Task created successfully');
      setShowTaskDialog(false);
      setTaskForm({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'medium',
        category: 'general',
        due_date: ''
      });
      fetchMyTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };
  
  const handleToggleTaskComplete = async (task) => {
    try {
      const newStatus = task.status === 'completed' ? 'todo' : 'completed';
      await marketingTasksAPI.update(task.id, { status: newStatus });
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
      fetchMyTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft', variant: 'secondary' },
      pending_review: { label: 'Pending Review', variant: 'outline' },
      legal_review: { label: 'Legal Review', variant: 'default' },
      manager_approval: { label: 'Awaiting Approval', variant: 'default' },
      approved: { label: 'Approved', variant: 'success' },
      scheduled: { label: 'Scheduled', variant: 'default' },
      live: { label: 'Live', variant: 'success' },
      paused: { label: 'Paused', variant: 'warning' },
      completed: { label: 'Completed', variant: 'secondary' },
      rejected: { label: 'Rejected', variant: 'destructive' }
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const campaigns = dashboardData?.campaigns || {};
  const legalReviews = dashboardData?.legal_reviews || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="marketing-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="h-6 w-6" />
            Marketing Hub
          </h1>
          <p className="text-muted-foreground">
            Manage campaigns, content, and marketing workflows
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchDashboardData(); fetchMyTasks(); }} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowTaskDialog(true)} data-testid="new-task-btn">
            <ListTodo className="h-4 w-4 mr-2" />
            New Task
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Link to="/marketing/campaigns/new">
              <Button data-testid="new-campaign-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Campaign
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
                <p className="text-3xl font-bold">{campaigns.total || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Megaphone className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Campaigns</p>
                <p className="text-3xl font-bold text-green-600">{campaigns.active || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <p className="text-3xl font-bold text-amber-600">{campaigns.pending_approval || 0}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">My Tasks</p>
                <p className="text-3xl font-bold">{taskStats.pending || 0}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <ListTodo className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue Tasks</p>
                <p className="text-3xl font-bold text-red-600">{taskStats.overdue || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My Tasks */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-purple-500" />
              My Tasks
            </CardTitle>
            <CardDescription>
              Tasks assigned to you
            </CardDescription>
          </CardHeader>
          <CardContent>
            {myTasks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
                <p>No pending tasks</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setShowTaskDialog(true)}>
                  Create Task
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {myTasks.map((task) => (
                    <div 
                      key={task.id} 
                      className="p-3 border rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          checked={task.status === 'completed'}
                          onCheckedChange={() => handleToggleTaskComplete(task)}
                          data-testid={`task-checkbox-${task.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                            {task.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge className={priorityColors[task.priority]} variant="secondary">
                              {task.priority}
                            </Badge>
                            <Badge className={categoryColors[task.category]} variant="outline">
                              {task.category}
                            </Badge>
                            {task.due_date && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {format(new Date(task.due_date), 'MMM d')}
                              </span>
                            )}
                          </div>
                          {task.campaign_name && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Campaign: {task.campaign_name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
        
        {/* Pending Approvals */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Pending Approvals
            </CardTitle>
            <CardDescription>
              Campaigns waiting for your review
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingApprovals.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-500" />
                <p>No pending approvals</p>
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {pendingApprovals.map((approval) => (
                    <Link 
                      key={approval.id} 
                      to={`/marketing/campaigns/${approval.entity_id}`}
                      className="block"
                    >
                      <div className="p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                        <p className="font-medium truncate">{approval.entity?.name || 'Campaign'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {approval.entity?.campaign_type || 'Campaign'}
                          </Badge>
                          {getStatusBadge(approval.entity?.status)}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Recent Campaigns */}
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Campaigns</CardTitle>
              <CardDescription>Latest marketing campaigns</CardDescription>
            </div>
            <Link to="/marketing/campaigns">
              <Button variant="outline" size="sm">
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {(!dashboardData?.recent_campaigns || dashboardData.recent_campaigns.length === 0) ? (
              <div className="text-center py-8 text-muted-foreground">
                <Megaphone className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No campaigns yet</p>
                {hasRole(['admin', 'marketing_manager']) && (
                  <Link to="/marketing/campaigns/new">
                    <Button variant="outline" size="sm" className="mt-2">
                      Create First Campaign
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-3">
                  {dashboardData.recent_campaigns.map((campaign) => (
                    <Link 
                      key={campaign.id} 
                      to={`/marketing/campaigns/${campaign.id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Megaphone className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{campaign.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {campaign.campaign_type?.replace('_', ' ')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(campaign.status)}
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/marketing/campaigns">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <Megaphone className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <h3 className="font-semibold">Campaigns</h3>
              <p className="text-sm text-muted-foreground">Manage campaigns</p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to="/marketing/calendar">
          <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6 text-center">
              <Calendar className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <h3 className="font-semibold">Content Calendar</h3>
              <p className="text-sm text-muted-foreground">Schedule content</p>
            </CardContent>
          </Card>
        </Link>
        
        {hasRole(['admin']) && (
          <Link to="/marketing/legal-rules">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="pt-6 text-center">
                <Scale className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                <h3 className="font-semibold">Legal Rules</h3>
                <p className="text-sm text-muted-foreground">Configure compliance</p>
              </CardContent>
            </Card>
          </Link>
        )}
        
        {hasRole(['admin']) && (
          <Link to="/marketing/workflows">
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardContent className="pt-6 text-center">
                <Target className="h-8 w-8 mx-auto mb-2 text-amber-600" />
                <h3 className="font-semibold">Workflows</h3>
                <p className="text-sm text-muted-foreground">Manage approvals</p>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      {/* Upcoming Calendar Items */}
      {dashboardData?.upcoming_calendar && dashboardData.upcoming_calendar.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Upcoming Schedule
              </CardTitle>
              <CardDescription>Content scheduled for the next few days</CardDescription>
            </div>
            <Link to="/marketing/calendar">
              <Button variant="outline" size="sm">
                View Calendar
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboardData.upcoming_calendar.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color || '#3b82f6' }}
                    />
                    <div>
                      <p className="font-medium">{item.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(item.scheduled_date), 'MMM d, yyyy')}
                        {item.scheduled_time && ` at ${item.scheduled_time}`}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="capitalize">
                    {item.item_type?.replace('_', ' ')}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Create Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>
              Assign a task to yourself or a team member
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="task-title">Title *</Label>
              <Input
                id="task-title"
                value={taskForm.title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Task title"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <Label htmlFor="task-desc">Description</Label>
              <Textarea
                id="task-desc"
                value={taskForm.description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Task details..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assign To</Label>
                <Select 
                  value={taskForm.assigned_to || 'self'} 
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, assigned_to: v === 'self' ? user?.sub : v }))}
                >
                  <SelectTrigger data-testid="task-assignee-select">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself</SelectItem>
                    {teamMembers.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={taskForm.priority} 
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={taskForm.category} 
                  onValueChange={(v) => setTaskForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="campaign">Campaign</SelectItem>
                    <SelectItem value="creative">Creative</SelectItem>
                    <SelectItem value="content">Content</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="social">Social Media</SelectItem>
                    <SelectItem value="analytics">Analytics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="task-due">Due Date</Label>
                <Input
                  id="task-due"
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={creatingTask} data-testid="create-task-submit">
              {creatingTask ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingDashboardPage;
